/** Справочник нативных пользователей: список, карта UUID → имя, один по id. */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchNativeUser, fetchNativeUsers } from '../api/users';
import type { NativeUserStatus } from '../api/users';
import type { NativeUserListItem } from './types';

export const nativeUsersKey = (status?: NativeUserStatus) =>
  ['native-users', status ?? 'active'] as const;

export function useNativeUsers(status?: NativeUserStatus) {
  return useQuery({
    queryKey: nativeUsersKey(status),
    queryFn: () => fetchNativeUsers(status),
    staleTime: 5 * 60 * 1000,
  });
}

export function useNativeUser(id: string | null | undefined) {
  return useQuery({
    queryKey: ['native-user', id],
    queryFn: () => fetchNativeUser(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Карта `uuid → пользователь` для списков задач: в `/v2/tasks` приходят голые
 * UUID, а рисовать надо имена.
 *
 * По умолчанию справочник тянется только по активным. Экраны, где встречаются
 * ссылки на отключённых и приглашённых (состав отдела, участники рабочей
 * группы), передают `'all'` — иначе вместо имени остаётся голый UUID.
 */
export function useNativeUsersMap(status?: NativeUserStatus) {
  const { data, isLoading } = useNativeUsers(status);

  const usersMap = useMemo(
    () => new Map((data?.result ?? []).map((user) => [user.id, user])),
    [data]
  );

  return useMemo(
    () => ({
      usersMap,
      isLoading,
      users: data?.result ?? [],
      getUserById: (id: string | null | undefined): NativeUserListItem | null =>
        (id ? usersMap.get(id) : null) ?? null,
      getDisplayNameById: (id: string | null | undefined): string =>
        (id ? usersMap.get(id)?.displayName : '') || '',
    }),
    [usersMap, isLoading, data]
  );
}
